import { apiRequest } from '../../../services/api/client';
import { AssignedClassTeacher, TeacherCandidate } from '../types/classroom.types';

type TeacherCandidateResource = {
  id: number;
  full_name: string;
  phone: string | null;
  ecclesiastical_role?: {
    name: string;
  } | null;
};

type ClassTeacherResource = {
  id: number;
  class_id: number;
  member_id: number;
  role: string;
  member?: {
    full_name: string;
    phone: string | null;
    ecclesiastical_role?: {
      name: string;
    } | null;
  } | null;
  class?: {
    name: string;
    segment?: {
      name: string;
    } | null;
  } | null;
};

type PaginatedResponse<T> = {
  data: T[];
  meta: {
    current_page: number;
    last_page: number;
    total: number;
    has_more_pages: boolean;
  };
};

type ListResponse<T> = {
  data: T[];
};

const toTeacherCandidate = (teacher: TeacherCandidateResource): TeacherCandidate => ({
  id: String(teacher.id),
  name: teacher.full_name,
  phone: teacher.phone ?? '',
  role: teacher.ecclesiastical_role?.name ?? 'Membro',
});

const toAssignedClassTeacher = (teacher: ClassTeacherResource): AssignedClassTeacher => ({
  id: String(teacher.id),
  memberId: String(teacher.member_id),
  name: teacher.member?.full_name ?? 'Membro sem nome',
  phone: teacher.member?.phone ?? '',
  role: teacher.role || teacher.member?.ecclesiastical_role?.name || 'Professor',
  classId: String(teacher.class_id),
  className: teacher.class?.name ?? 'Turma não informada',
  segmentName: teacher.class?.segment?.name ?? 'Sem segmento',
});

export const classTeacherService = {
  async listCandidates(search?: string): Promise<TeacherCandidate[]> {
    const params = new URLSearchParams({ per_page: '250' });
    if (search?.trim()) {
      params.set('q', search.trim());
    }

    const payload = await apiRequest<PaginatedResponse<TeacherCandidateResource>>(
      `/ebd/teachers/candidates?${params.toString()}`
    );

    return payload.data.map(toTeacherCandidate);
  },

  async listAssignedTeachers(): Promise<AssignedClassTeacher[]> {
    const payload = await apiRequest<PaginatedResponse<ClassTeacherResource>>('/ebd/teachers?per_page=100');
    return payload.data.map(toAssignedClassTeacher);
  },

  async listByClass(classId: string, trimesterId?: string): Promise<AssignedClassTeacher[]> {
    const params = new URLSearchParams();
    if (trimesterId) {
      params.set('quarter_id', trimesterId);
    }

    const query = params.toString();
    const payload = await apiRequest<ListResponse<ClassTeacherResource>>(
      `/ebd/classes/${classId}/teachers${query ? `?${query}` : ''}`
    );

    return payload.data.map(toAssignedClassTeacher);
  },
};
